// Copyright 2025. WebPros International GmbH. All rights reserved.

import {
    IPaginateApiResponse,
    paginateInitialState,
} from 'common/api/resources/Response';
import {
    DisasterRecoveryEntityStatus,
    DisasterRecoveryStatus,
    IDisasterRecoveryEntityResponse,
    IDisasterRecoveryResponse,
} from 'common/api/resources/DisasterRecovery';
import {
    ActionType,
    getType,
} from 'typesafe-actions';
import * as disasterRecoveryActions from 'common/modules/disasterRecovery/actions';
import { combineReducers } from 'redux';
import { initialShortComputeResourceState } from 'admin/computeResource/reducers';
import { initialShortComputeResourceVmState } from 'common/modules/computeResourceVm/reducers';
import { initialTaskState } from 'admin/task/reducer';
import { getPaginatedResponse } from 'common/tests/responses';
import {
    appendToPaginated,
    updateInPaginated,
} from 'common/reducers/list';

export interface IDisasterRecoveryHistoryReducer {
    list: IPaginateApiResponse<IDisasterRecoveryResponse[]>;
    entities: IPaginateApiResponse<IDisasterRecoveryEntityResponse[]>;
}

export type DrActions = ActionType<typeof disasterRecoveryActions>;
export type DrState = Readonly<IDisasterRecoveryHistoryReducer>;

export const initialDisasterRecoveryEntityState: IDisasterRecoveryEntityResponse = {
    id: 0,
    status: DisasterRecoveryEntityStatus.COMPLETED,
    target_compute_resource: undefined,
    compute_resource_vm: initialShortComputeResourceVmState,
    task: initialTaskState,
    started_at: '2025-01-01 00:00:00',
    finished_at: '2025-01-01 00:00:00',
    is_original_data_deleted: false,
    original_data: '{}',
    is_migrated_back: false,
    disaster_recovery: {
        id: 0,
        status: DisasterRecoveryStatus.COMPLETED,
        source_compute_resource: initialShortComputeResourceState,
        started_at: '2025-01-01 00:00:00',
        finished_at: '2025-01-01 00:00:00',
        is_loading: false,
        entities_count: {
            total: 1,
            running_entities_count: 0,
        },
    },
    fail_reason: 'fail reason',
};

export const initialDisasterRecoveryState: IDisasterRecoveryResponse = {
    id: 0,
    status: DisasterRecoveryStatus.COMPLETED,
    source_compute_resource: initialShortComputeResourceState,
    started_at: '2025-01-01 00:00:00',
    finished_at: '2025-01-01 00:00:00',
    is_loading: false,
    entities: getPaginatedResponse([initialDisasterRecoveryEntityState]),
    entities_count: {
        total: 1,
        running_entities_count: 0,
    },
};

export default combineReducers<DrState, DrActions>({
    list: (state = paginateInitialState, action) => {
        switch (action.type) {
        case getType(disasterRecoveryActions.setDisasterRecoveryHistory):
            return action.payload;
        case getType(disasterRecoveryActions.appendDisasterRecovery):
            return ({
                ...state,
                data: [...state.data, action.payload],
            });
        case getType(disasterRecoveryActions.setDisasterRecoveryEntitiesForProcess):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (action.payload.processId === process.id) {
                        process.entities = action.payload.data;
                    }

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.setDisasterRecoveryProcessLoading):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (action.payload === process.id) {
                        process.is_loading = true;
                    }

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.unsetDisasterRecoveryProcessLoading):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (action.payload === process.id) {
                        process.is_loading = false;
                    }

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.setOriginalDataManuallyDeletedEntityIds):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (!process.entities) {
                        return process;
                    }

                    process.entities.data = process.entities.data.map(entity => {
                        if (action.payload.includes(entity.id)) {
                            entity.is_original_data_deleted = true;
                        }

                        return entity;
                    });

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.setDisasterRecoveryEntitiesDeletingOriginalData):
            return ({
                ...state,
                data: state.data.map(process => {
                    process.entities?.data.forEach(entity => {
                        if (action.payload.disaster_recovery_entity_ids?.includes(entity.id)
                                || action.payload.disaster_recovery_ids?.includes(process.id)
                        ) {
                            entity.is_deleting_original_data = true;
                        }

                        return entity;
                    });

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.unsetDisasterRecoveryEntitiesDeletingOriginalData):
            return ({
                ...state,
                data: state.data.map(process => {
                    process.entities?.data.forEach(entity => {
                        if (action.payload.disaster_recovery_entity_ids?.includes(entity.id)
                                || action.payload.disaster_recovery_ids?.includes(process.id)
                        ) {
                            entity.is_deleting_original_data = false;
                        }

                        return entity;
                    });

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.appendDisasterRecoveryEntities):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (action.payload.processId === process.id && process.entities) {
                        process.entities = appendToPaginated(process.entities, action.payload.data);
                    }

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.updateDisasterRecoveryEntity):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (process.entities && process.id === action.payload.disaster_recovery.id) {
                        process = {
                            ...process,
                            entities: updateInPaginated(process.entities, action.payload, 'id'),
                        };

                        process.status = updateProcessStatus(process);
                    }

                    return process;
                }),
            });
        case getType(disasterRecoveryActions.updateDisasterRecoveryEntityTask):
            return ({
                ...state,
                data: state.data.map(process => {
                    if (process.entities) {
                        return {
                            ...process,
                            entities: {
                                ...process.entities,
                                data: process.entities.data.map(entity => {
                                    if (entity.task?.id === action.payload.taskId) {
                                        return {
                                            ...entity,
                                            task: {
                                                ...entity.task,
                                                progress: action.payload.progress,
                                            },
                                        };
                                    }

                                    return entity;
                                }),
                            },
                        };
                    }

                    return process;
                }),
            });
        default:
            return state;
        }
    },
    entities: (state = paginateInitialState, action) => {
        switch (action.type) {
        case getType(disasterRecoveryActions.setDisasterRecoveryEntities):
            return action.payload;
        case getType(disasterRecoveryActions.updateDisasterRecoveryEntity):
            return updateInPaginated(state, action.payload, 'id');
        case getType(disasterRecoveryActions.updateDisasterRecoveryEntityTask):
            return {
                ...state,
                data: state.data.map(entity => {
                    if (entity.task?.id === action.payload.taskId) {
                        return {
                            ...entity,
                            task: {
                                ...entity.task,
                                progress: action.payload.progress,
                            },
                        };
                    }
                    return entity;
                }),
            };
        default:
            return state;
        }
    },
});

const updateProcessStatus = (process: IDisasterRecoveryResponse) => {
    if (process.entities && process.entities.data.length > 0) {
        const statuses = process.entities.data.map(entity => entity.status);
        const hasRunning = statuses.includes(DisasterRecoveryEntityStatus.RUNNING);
        const hasPending = statuses.includes(DisasterRecoveryEntityStatus.PENDING);
        const hasFailed = statuses.includes(DisasterRecoveryEntityStatus.FAILED);

        if (hasFailed && (hasRunning || hasPending)) {
            return DisasterRecoveryStatus.RUNNING_WITH_ERRORS;
        } else if (hasRunning) {
            return DisasterRecoveryStatus.RUNNING;
        } else if (hasFailed) {
            return DisasterRecoveryStatus.FAILED;
        } else if (hasPending) {
            return DisasterRecoveryStatus.PENDING;
        }

        return DisasterRecoveryStatus.COMPLETED;
    }

    return process.status;
};
